import { useState, useEffect, useMemo, useCallback } from 'react';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import Toast from '../../../components/v2/Toast.jsx';
import useToast from '../../../hooks/useToast.js';
import {
    getSession,
    getCampusId,
    getUserId,
    getSessionId,
    getCampusList,
    updateSessionCampus,
} from '../../../services/session.js';
import { apiMqtt } from '../../../services/api.js';
import { iAmHigherAuthority } from '../../../libs.jsx';
import {
    fetchLightControllers,
    fetchChannelTypes,
    fetchAreas,
    fetchDevices,
    addLightController,
    editChannel,
    deleteLightController,
} from './api/lightControllerApi.js';

import ChannelTable from './components/ChannelTable.jsx';
import ChannelDrawer from './components/ChannelDrawer.jsx';

import './light-controller-settings-page.css';
import Drawer from '../../../components/v2/Drawer.jsx';

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
const namePattern = /^[a-zA-Z0-9_ \-]+$/;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const LightControllerSettingsPage = () => {
    // -- Session --
    const [campusId, setCampusId] = useState(() => getCampusId());
    const session = useMemo(() => getSession(), []);

    const campusList = useMemo(() => {
        return getCampusList() || [];
    }, []);

    const campusName = useMemo(() => {
        const match = campusList.find((c) => c.campus_id === campusId);
        return match?.campus_name || `Campus ${campusId}`;
    }, [campusId, campusList]);

    // Permissions
    const editRights = useMemo(() => iAmHigherAuthority(20, session?.role_weight), [session]);
    const adminRights = useMemo(() => iAmHigherAuthority(99, session?.role_weight), [session]);

    // -- Data stores --
    const [lightData, setLightData] = useState([]);
    const [channelTypes, setChannelTypes] = useState([]);
    const [areas, setAreas] = useState([]);
    const [deviceList, setDeviceList] = useState([]);

    // -- Loading / Saving --
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [restoring, setRestoring] = useState(false);

    // -- Active tab (controller ID) --
    const [activeTab, setActiveTab] = useState(null);

    // -- Add Controller Drawer --
    const [addDrawerOpen, setAddDrawerOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addTypeId, setAddTypeId] = useState('');
    const [addLcId, setAddLcId] = useState('');
    const [addAreaId, setAddAreaId] = useState('');
    const [addErrors, setAddErrors] = useState({});

    // -- Edit Channel Drawer --
    const [channelDrawerOpen, setChannelDrawerOpen] = useState(false);
    const [channelEditTarget, setChannelEditTarget] = useState(null);

    // -- Delete Controller Dialog --
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // -- Toast --
    const { toast, showToast: showTypedToast, dismissToast } = useToast();
    /* This page's calls are (message, type); the shared hook is (type, message). */
    const showToast = useCallback((message, type = 'success') => showTypedToast(type, message), [showTypedToast]);

    // ======================================================================
    // Data loading
    // ======================================================================
    const loadData = useCallback(async (cId) => {
        setLoading(true);
        try {
            const [controllersRes, typesRes, areasRes, devicesRes] = await Promise.all([
                fetchLightControllers(cId),
                fetchChannelTypes(),
                fetchAreas(cId),
                fetchDevices(cId),
            ]);

            const controllers = controllersRes || [];
            setLightData(controllers);
            setChannelTypes(typesRes || []);
            setAreas(areasRes || []);
            setDeviceList(devicesRes || []);

            // Default tab to first controller
            if (controllers.length > 0) {
                setActiveTab((prev) => {
                    const stillExists = controllers.find((lc) => lc.light_controller_id === prev);
                    return stillExists ? prev : controllers[0].light_controller_id;
                });
            } else {
                setActiveTab(null);
            }
        } catch (err) {
            console.error('[LightControllerSettingsPage] Load error:', err);
            showToast('Failed to load light controller data.', 'error');
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    // Initial load + reload on campus switch
    useEffect(() => {
        loadData(campusId);
    }, [campusId, loadData]);

    // ======================================================================
    // Campus switch
    // ======================================================================
    const handleCampusChange = (newId) => {
        setCampusId(newId);
        updateSessionCampus(newId);

        const oldIsCloud = session?.campusData?.[campusId?.toString()]?.is_cloud;
        const newIsCloud = session?.campusData?.[newId?.toString()]?.is_cloud;
        if (oldIsCloud !== newIsCloud) {
            window.location.reload();
        }
    };

    // ======================================================================
    // MQTT Operations — Sync Local & Restore Data
    // ======================================================================
    const handleSyncLocal = async () => {
        setSyncing(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'reboot-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Sync triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error syncing. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error syncing. Contact support if the error persists.', 'error');
        } finally {
            setSyncing(false);
        }
    };

    const handleRestoreData = async () => {
        setRestoring(true);
        try {
            const [ok] = await apiMqtt({
                operation: 'restore-server',
                campusId,
                clientId: getSessionId(),
            });
            if (ok) {
                showToast('Restore triggered. Please allow up to 3 minutes for changes to take effect.');
            } else {
                showToast('Error restoring. Contact support if the error persists.', 'error');
            }
        } catch {
            showToast('Error restoring. Contact support if the error persists.', 'error');
        } finally {
            setRestoring(false);
        }
    };

    // ======================================================================
    // CRUD meta
    // ======================================================================
    const meta = useMemo(() => ({
        campusId,
        userId: getUserId(),
        clientId: getSessionId(),
    }), [campusId]);

    // ======================================================================
    // Add Controller
    // ======================================================================
    const resetAddForm = () => {
        setAddName('');
        setAddTypeId('');
        setAddLcId('');
        setAddAreaId('');
        setAddErrors({});
    };

    const openAddDrawer = () => {
        resetAddForm();
        setAddDrawerOpen(true);
    };

    const validateAddForm = () => {
        const errs = {};
        if (!addName.trim()) {
            errs.name = 'Controller name is required.';
        } else if (!namePattern.test(addName.trim())) {
            errs.name = 'Only letters, numbers, spaces, and - _ are allowed.';
        }
        if (!addTypeId) {
            errs.typeId = 'Controller type is required.';
        }
        if (!addLcId || parseInt(addLcId) < 1 || parseInt(addLcId) > 100) {
            errs.lcId = 'Controller ID is required (1–100).';
        }
        if (!addAreaId) {
            errs.areaId = 'Area is required.';
        }
        setAddErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const clearAddError = (field) => {
        setAddErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleAddSubmit = async () => {
        if (!validateAddForm()) return;

        const selectedType = channelTypes.find((ct) => ct.light_channel_type_id === parseInt(addTypeId));
        const maxChannels = selectedType?.max_channels || 32;

        const payload = [{
            campus_id: campusId,
            lc_id: parseInt(addLcId),
            description: addName.trim(),
            channel_type_id: parseInt(addTypeId),
            area_id: parseInt(addAreaId),
            max_channels: maxChannels,
        }];

        setSaving(true);
        try {
            const [ok] = await addLightController(payload, meta);
            if (ok) {
                setAddDrawerOpen(false);
                showToast(`Light controller "${addName.trim()}" has been added.`);
                await loadData(campusId);
            } else {
                showToast('Failed to add light controller.', 'error');
            }
        } catch {
            showToast('Failed to add light controller.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Delete Controller
    // ======================================================================
    const activeController = useMemo(
        () => lightData.find((lc) => lc.light_controller_id === activeTab) || null,
        [lightData, activeTab]
    );

    const handleConfirmDelete = async () => {
        if (!activeController) return;
        setDeleteDialogOpen(false);
        setSaving(true);
        try {
            const payload = [{ campus_id: campusId, light_controller_id: activeController.light_controller_id }];
            const [ok] = await deleteLightController(payload, meta);
            if (ok) {
                showToast(`Controller "${activeController.description}" deleted.`);
                await loadData(campusId);
            } else {
                showToast('Failed to delete controller.', 'error');
            }
        } catch {
            showToast('Failed to delete controller.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Edit Channel
    // ======================================================================
    const handleEditChannel = (channelRow) => {
        setChannelEditTarget(channelRow);
        setChannelDrawerOpen(true);
    };

    const handleSaveChannel = async (payload) => {
        setSaving(true);
        try {
            const [ok] = await editChannel(payload, meta);
            if (ok) {
                setChannelDrawerOpen(false);
                setChannelEditTarget(null);
                showToast('Channel updated.');
                await loadData(campusId);
            } else {
                showToast('Failed to update channel.', 'error');
            }
        } catch {
            showToast('Failed to update channel.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ======================================================================
    // Dynamic tab list
    // ======================================================================
    const tabs = useMemo(
        () => lightData.map((lc) => ({
            key: lc.light_controller_id,
            label: lc.description || `Controller ${lc.lc_id}`,
        })),
        [lightData]
    );

    // ======================================================================
    // Render helpers
    // ======================================================================
    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="pp-section">
                    <div className="pp-section__body">
                        <div className="ss-skeleton" style={{ width: '60%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '80%', height: '16px', marginBottom: 'var(--space-3)' }} />
                        <div className="ss-skeleton" style={{ width: '100%', height: '44px' }} />
                    </div>
                </div>
            );
        }

        // No controllers exist
        if (lightData.length === 0) {
            return (
                <div className="ss-empty">
                    <svg className="ss-empty__icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                    </svg>
                    <p className="ss-empty__text">No light controllers configured for this campus.</p>
                    <button className="dp-btn dp-btn--primary" onClick={openAddDrawer}>
                        <i className="fe fe-plus" style={{ marginRight: '0.5rem' }} />
                        Add Controller
                    </button>
                </div>
            );
        }

        // Active controller
        if (!activeController) return null;

        return (
            <>
                {/* Controller info header */}
                <div className="lc-controller-info">
                    <span className="lc-controller-info__type">
                        Type: <strong>{activeController.light_channel_type_name || '—'}</strong>
                    </span>
                    <span className="lc-controller-info__area">
                        Area: <strong>{activeController.area_name || '—'}</strong>
                    </span>
                    <span className="lc-controller-info__channels">
                        Channels: <strong>{activeController.max_channels || '—'}</strong>
                    </span>
                </div>

                <ChannelTable
                    channels={activeController.channels || []}
                    controllerName={activeController.description}
                    onEdit={handleEditChannel}
                    saving={saving}
                />
            </>
        );
    };

    // ======================================================================
    // Render
    // ======================================================================
    return (
        <>
            <NavigationV2 />
            <div className="nav2-main">
                <div className="ss-page">
                    {/* Toast notification */}
                    <Toast toast={toast} onDismiss={dismissToast} />

                    {/* Page header */}
                    <div className="ps-page-header">
                        <div className="ps-page-header__inner">
                            <div className="ps-breadcrumb-row">
                                <nav className="ps-breadcrumb">
                                    <span className="ps-breadcrumb__text">Settings</span>
                                    <span className="ps-breadcrumb__separator">/</span>
                                    <span className="ps-breadcrumb__current">Light Controller Settings</span>
                                </nav>
                                <div className="ps-campus-row">
                                    {/* MQTT action buttons */}
                                    {editRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleSyncLocal}
                                            disabled={syncing}
                                            title="Syncs local server with cloud data, will cause the server to go offline momentarily during the process"
                                        >
                                            {syncing ? 'Syncing...' : 'Sync Local'}
                                        </button>
                                    ) : null}
                                    {adminRights ? (
                                        <button
                                            className="ap-btn ap-btn--secondary"
                                            onClick={handleRestoreData}
                                            disabled={restoring}
                                            title="Restore data performs the sync local reboot, however deletes the database file triggering a re-create."
                                        >
                                            {restoring ? 'Restoring...' : 'Restore Data'}
                                        </button>
                                    ) : null}

                                    {/* Campus selector */}
                                    <label htmlFor="lc-campus-selector" className="ps-campus-label">Campus:</label>
                                    {campusList.length > 1 ? (
                                        <select
                                            id="lc-campus-selector"
                                            value={campusId}
                                            onChange={(e) => handleCampusChange(parseInt(e.target.value, 10))}
                                            className="ps-campus-select"
                                        >
                                            {campusList.map((c) => (
                                                <option key={c.campus_id} value={c.campus_id}>
                                                    {c.campus_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="ps-campus-name">{campusName}</span>
                                    )}
                                </div>
                            </div>

                            {/* Page-level action buttons */}
                            {lightData.length > 0 ? (
                                <div className="lc-page-actions">
                                    <button className="dp-btn dp-btn--danger-outline" onClick={() => setDeleteDialogOpen(true)} disabled={!activeController}>
                                        <i className="fe fe-trash-2" style={{ marginRight: '0.5rem' }} />
                                        Delete Controller
                                    </button>
                                    <button className="dp-btn dp-btn--primary" onClick={openAddDrawer}>
                                        <i className="fe fe-plus" style={{ marginRight: '0.5rem' }} />
                                        Add Controller
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Dynamic tab bar */}
                    {tabs.length > 0 ? (
                        <div className="pp-tab-bar">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
                                    onClick={() => setActiveTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {/* Scrollable tab content */}
                    <div className="pp-tab-content">
                        {renderTabContent()}
                    </div>

                    {/* Add Controller Drawer */}
                    <Drawer
                        open={addDrawerOpen}
                        onClose={() => setAddDrawerOpen(false)}
                        title="Add Light Controller"
                        footer={(
                            <>
                                <button className="dp-btn dp-btn--cancel" onClick={() => setAddDrawerOpen(false)}>Cancel</button>
                                <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleAddSubmit}>
                                    {saving ? 'Adding...' : 'Add Controller'}
                                </button>
                            </>
                        )}
                    >
                        {/* Controller Name */}
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Controller Name <span className="dp-drawer__required">*</span>
                            </label>
                            <input
                                className={`dp-drawer__input${addErrors.name ? ' is-invalid' : ''}`}
                                type="text"
                                value={addName}
                                onChange={(e) => { setAddName(e.target.value); clearAddError('name'); }}
                                placeholder="e.g. Main Building LC1"
                            />
                            {addErrors.name ? <p className="dp-drawer__error">{addErrors.name}</p> : null}
                        </div>

                        {/* Controller Type */}
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Controller Type <span className="dp-drawer__required">*</span>
                            </label>
                            <select
                                className={`dp-drawer__input${addErrors.typeId ? ' is-invalid' : ''}`}
                                value={addTypeId}
                                onChange={(e) => { setAddTypeId(e.target.value); clearAddError('typeId'); }}
                            >
                                <option value="">Select type...</option>
                                {channelTypes.map((ct) => (
                                    <option key={ct.light_channel_type_id} value={ct.light_channel_type_id}>
                                        {ct.light_channel_type_name}
                                    </option>
                                ))}
                            </select>
                            {addErrors.typeId ? <p className="dp-drawer__error">{addErrors.typeId}</p> : null}
                            <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                Physical type of dome light controller being installed.
                            </p>
                        </div>

                        {/* Controller ID */}
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Controller ID <span className="dp-drawer__required">*</span>
                            </label>
                            <input
                                className={`dp-drawer__input${addErrors.lcId ? ' is-invalid' : ''}`}
                                type="number"
                                min={1}
                                max={100}
                                step={1}
                                value={addLcId}
                                onChange={(e) => { setAddLcId(e.target.value); clearAddError('lcId'); }}
                                placeholder="e.g. 1"
                            />
                            {addErrors.lcId ? <p className="dp-drawer__error">{addErrors.lcId}</p> : null}
                            <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                This is the dome light server code (e.g. LC1, LC2...).
                            </p>
                        </div>

                        {/* Area */}
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">
                                Area <span className="dp-drawer__required">*</span>
                            </label>
                            <select
                                className={`dp-drawer__input${addErrors.areaId ? ' is-invalid' : ''}`}
                                value={addAreaId}
                                onChange={(e) => { setAddAreaId(e.target.value); clearAddError('areaId'); }}
                            >
                                <option value="">Select area...</option>
                                {areas.map((a) => (
                                    <option key={a.area_id} value={a.area_id}>
                                        {a.area_name}
                                    </option>
                                ))}
                            </select>
                            {addErrors.areaId ? <p className="dp-drawer__error">{addErrors.areaId}</p> : null}
                            <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                                Location where the dome light box is being installed.
                            </p>
                        </div>
                    </Drawer>

                    {/* Edit Channel Drawer */}
                    <ChannelDrawer
                        open={channelDrawerOpen}
                        onClose={() => { setChannelDrawerOpen(false); setChannelEditTarget(null); }}
                        onSave={handleSaveChannel}
                        saving={saving}
                        editTarget={channelEditTarget}
                        controllerType={activeController?.light_channel_type_id || 1}
                        devices={deviceList}
                        controllerId={activeController?.light_controller_id}
                        campusId={campusId}
                    />

                    {/* Delete Controller Dialog */}
                    {deleteDialogOpen ? (
                        <div className="pp-dialog-backdrop" onClick={() => setDeleteDialogOpen(false)}>
                            <div className="pp-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="pp-dialog__header">
                                    <h3 className="pp-dialog__title">Delete Controller</h3>
                                    <button className="dp-drawer__close" onClick={() => setDeleteDialogOpen(false)}>
                                        <i className="fe fe-x" />
                                    </button>
                                </div>
                                <div className="pp-dialog__body">
                                    <p className="pp-dialog__text">
                                        Are you sure you want to delete <strong>{activeController?.description}</strong> and all its channels? This action cannot be undone.
                                    </p>
                                </div>
                                <div className="pp-dialog__footer">
                                    <button className="dp-btn dp-btn--cancel" onClick={() => setDeleteDialogOpen(false)}>
                                        Cancel
                                    </button>
                                    <button className="dp-btn dp-btn--danger" disabled={saving} onClick={handleConfirmDelete}>
                                        {saving ? 'Deleting...' : 'Delete Controller'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default LightControllerSettingsPage;
